import axios from 'axios';

import {
  ERROR_REGISTRY,
  type ErrorCode,
  type ErrorResponse,
  type InvalidParametersData,
  isErrorCode,
} from '@d3/contract';

import { REQUEST_ID_HEADER } from './api.constant';

/** 서버가 주지 않는, 클라이언트가 만든 코드. 계약에 넣지 않는다(FE 전용). */
export const CLIENT_ERROR_CODE = {
  NETWORK_ERROR: 'NETWORK_ERROR',
  TIMEOUT: 'TIMEOUT',
  CANCELED: 'CANCELED',
} as const;

export type ClientErrorCode = (typeof CLIENT_ERROR_CODE)[keyof typeof CLIENT_ERROR_CODE];
export type ApiErrorCode = ErrorCode | ClientErrorCode;

export interface ApiErrorInit<D = unknown> {
  code: ApiErrorCode;
  /** HTTP status. 응답을 받지 못했으면 0 */
  status: number;
  message: string;
  data?: D;
  traceId?: string;
  cause?: unknown;
}

/**
 * 모든 요청 실패의 단일 형태. 화면은 `message`(서버 · 개발자 문구)를 그대로 보이지 않고
 * `getErrorMessage(error)` 로 사용자 문구를 얻는다.
 */
export class ApiError<D = unknown> extends Error {
  readonly code: ApiErrorCode;
  readonly status: number;
  readonly data?: D;
  readonly traceId?: string;

  constructor(init: ApiErrorInit<D>) {
    super(init.message, init.cause === undefined ? undefined : { cause: init.cause });
    this.name = 'ApiError';
    this.code = init.code;
    this.status = init.status;
    if (init.data !== undefined) this.data = init.data;
    if (init.traceId !== undefined) this.traceId = init.traceId;
  }

  get isClientSide(): boolean {
    return this.status === 0;
  }
}

/**
 * 코드 분기는 이 헬퍼로만 한다. 문자열 비교를 흩뿌리지 않는다.
 * `isApiError(err)` · `isApiError(err, 'CONFLICT')` · `isApiError(err, ['NOT_FOUND', 'FORBIDDEN'])`
 */
export function isApiError(error: unknown): error is ApiError;
export function isApiError<C extends ApiErrorCode>(
  error: unknown,
  code: C | readonly C[],
): error is ApiError & { code: C };
export function isApiError(error: unknown, code?: ApiErrorCode | readonly ApiErrorCode[]): boolean {
  if (!(error instanceof ApiError)) return false;
  if (code === undefined) return true;
  return typeof code === 'string' ? error.code === code : code.includes(error.code);
}

export function isInvalidParametersError(
  error: unknown,
): error is ApiError<InvalidParametersData> & { data: InvalidParametersData } {
  if (!isApiError(error, 'BAD_REQUEST_INVALID_PARAMETERS')) return false;
  const data = error.data as Partial<InvalidParametersData> | undefined;
  return Array.isArray(data?.fields);
}

/**
 * 봉투가 아닌 응답(프록시 · 게이트웨이 · 옛 서버)의 status → 코드.
 * 503 은 넣지 않는다: 점검 화면은 서버가 봉투로 `SERVICE_UNAVAILABLE` 을 줄 때만 띄운다.
 */
const STATUS_TO_CODE: Readonly<Record<number, ErrorCode>> = {
  401: 'UNAUTHORIZED',
  403: 'FORBIDDEN',
  404: 'NOT_FOUND',
  409: 'CONFLICT',
  413: 'PAYLOAD_TOO_LARGE',
  429: 'TOO_MANY_REQUESTS',
};

function isEnvelope(body: unknown): body is Partial<ErrorResponse> & { code: ErrorCode } {
  return (
    typeof body === 'object' && body !== null && isErrorCode((body as { code?: unknown }).code)
  );
}

function readHeader(headers: unknown, name: string): string | undefined {
  if (typeof headers !== 'object' || headers === null) return undefined;
  const getter = (headers as { get?: (key: string) => unknown }).get;
  const value =
    typeof getter === 'function'
      ? getter.call(headers, name)
      : (headers as Record<string, unknown>)[name];
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

/** 어떤 실패든 `ApiError` 로 바꾼다. */
export function normalizeError(error: unknown): ApiError {
  if (error instanceof ApiError) return error;

  if (axios.isCancel(error)) {
    return new ApiError({
      code: CLIENT_ERROR_CODE.CANCELED,
      status: 0,
      message: 'Request canceled',
      cause: error,
    });
  }

  if (axios.isAxiosError(error)) {
    const requestId = readHeader(error.config?.headers, REQUEST_ID_HEADER);
    const response = error.response;

    if (!response) {
      const timedOut = error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT';
      return new ApiError({
        code: timedOut ? CLIENT_ERROR_CODE.TIMEOUT : CLIENT_ERROR_CODE.NETWORK_ERROR,
        status: 0,
        message: timedOut ? 'Request timed out' : 'Network error',
        ...(requestId ? { traceId: requestId } : {}),
        cause: error,
      });
    }

    const body: unknown = response.data;
    const traceId =
      (isEnvelope(body) && typeof body.traceId === 'string' ? body.traceId : undefined) ??
      readHeader(response.headers, REQUEST_ID_HEADER) ??
      requestId;

    if (isEnvelope(body)) {
      return new ApiError({
        code: body.code,
        status: response.status,
        message:
          typeof body.message === 'string' ? body.message : ERROR_REGISTRY[body.code].message,
        ...(body.data !== undefined ? { data: body.data } : {}),
        ...(traceId ? { traceId } : {}),
        cause: error,
      });
    }

    const code = STATUS_TO_CODE[response.status] ?? 'UNKNOWN_ERROR';
    return new ApiError({
      code,
      status: response.status,
      message: ERROR_REGISTRY[code].message,
      ...(traceId ? { traceId } : {}),
      cause: error,
    });
  }

  return new ApiError({
    code: 'UNKNOWN_ERROR',
    status: 0,
    message: error instanceof Error ? error.message : 'Unexpected error',
    cause: error,
  });
}
